import { Head, usePage } from '@inertiajs/react'
import {
  Badge,
  Card,
  Container,
  Group,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
} from '@mantine/core'
import { Activity, Clock, Sliders } from 'lucide-react'
import { timeAgo } from '@/lib/session-utils'
import SettingsForm from '@/components/SettingsForm'
import type {
  MeteredResourceProps,
  OrganizationSettingsPageProps,
  UsageEventProps,
} from '@/types'

function UsageSummaryCards({ summary }: { summary: Record<string, number> }) {
  const entries = Object.entries(summary)
  if (entries.length === 0) return <Text c="dimmed">No usage events recorded yet.</Text>

  return (
    <Group gap="md">
      {entries.map(([type, count]) => (
        <Card key={type} withBorder padding="lg" style={{ minWidth: 180 }}>
          <Text size="sm" c="dimmed" tt="uppercase" fw={600}>{type.replace(/_/g, ' ')}</Text>
          <Text size="xl" fw={700} mt={4}>{count.toLocaleString()}</Text>
        </Card>
      ))}
    </Group>
  )
}

function formatMetadata(metadata: Record<string, unknown>): string {
  const entries = Object.entries(metadata)
  if (entries.length === 0) return '-'
  return entries
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join(', ')
}

function formatDuration(startedAt: string, stoppedAt: string | null): string {
  const start = new Date(startedAt).getTime()
  const end = stoppedAt ? new Date(stoppedAt).getTime() : Date.now()
  const seconds = Math.floor((end - start) / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ${minutes % 60}m`
}

function UsageEventsTable({ events }: { events: UsageEventProps[] }) {
  if (events.length === 0) return <Text c="dimmed">No usage events recorded yet.</Text>

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Type</Table.Th>
          <Table.Th>Session</Table.Th>
          <Table.Th>Details</Table.Th>
          <Table.Th>Recorded</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {events.map((event) => (
          <Table.Tr key={event.id}>
            <Table.Td>
              <Badge variant="light" size="sm">{event.event_type}</Badge>
            </Table.Td>
            <Table.Td>
              <Text size="sm" c="dimmed">{event.session_title || '-'}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm" lineClamp={1}>{formatMetadata(event.metadata)}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm" c="dimmed">{timeAgo(event.recorded_at)}</Text>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )
}

function MeteredResourcesTable({ resources }: { resources: MeteredResourceProps[] }) {
  if (resources.length === 0) return <Text c="dimmed">No metered resources recorded yet.</Text>

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Type</Table.Th>
          <Table.Th>Resource ID</Table.Th>
          <Table.Th>Session</Table.Th>
          <Table.Th>Duration</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th>Started</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {resources.map((resource) => (
          <Table.Tr key={resource.id}>
            <Table.Td>
              <Badge variant="light" size="sm">{resource.resource_type}</Badge>
            </Table.Td>
            <Table.Td>
              <Text size="sm" ff="monospace">{resource.resource_id}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm" c="dimmed">{resource.session_title || '-'}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{formatDuration(resource.started_at, resource.stopped_at)}</Text>
            </Table.Td>
            <Table.Td>
              <Badge color={resource.active ? 'green' : 'gray'} variant="light" size="sm">
                {resource.active ? 'Active' : 'Stopped'}
              </Badge>
            </Table.Td>
            <Table.Td>
              <Text size="sm" c="dimmed">{timeAgo(resource.started_at)}</Text>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )
}



export default function OrganizationSettings() {
  const {
    organization,
    settings,
    sensitive_configured,
    settings_registry,
    usage_events,
    metered_resources,
    usage_summary,
  } = usePage<OrganizationSettingsPageProps>().props

  return (
    <>
      <Head title="Organization Settings" />

      <Container size="xl" py="md">
        <Stack gap="xl">
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Organization Settings</Title>
              <Text c="dimmed" mt={4}>{organization.name}</Text>
            </div>
            <Badge variant="light" size="lg">
              {organization.slug}
            </Badge>
          </Group>

          <Tabs defaultValue="settings">
            <Tabs.List>
              <Tabs.Tab value="settings" leftSection={<Sliders size={16} />}>
                Settings
              </Tabs.Tab>
              <Tabs.Tab value="usage" leftSection={<Activity size={16} />}>
                Usage
              </Tabs.Tab>
              <Tabs.Tab value="resources" leftSection={<Clock size={16} />}>
                Metered Resources ({metered_resources.length})
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="settings" pt="md">
              <SettingsForm
                registry={settings_registry}
                settings={settings}
                sensitiveConfigured={sensitive_configured}
                submitUrl={`/orgs/${organization.slug}/settings`}
              />
            </Tabs.Panel>

            <Tabs.Panel value="usage" pt="md">
              <Stack gap="md">
                <UsageSummaryCards summary={usage_summary} />
                <UsageEventsTable events={usage_events} />
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="resources" pt="md">
              <MeteredResourcesTable resources={metered_resources} />
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Container>
    </>
  )
}
